import type { WeatherGapInfo } from '../../types/simulation';

const normalizeCoordinates = (value: unknown): WeatherGapInfo['coordinates'] => {
  if (!value) return null;

  const toFinite = (input: unknown): number | null => {
    const num = Number(input);
    return Number.isFinite(num) ? num : null;
  };

  const build = (latitude: unknown, longitude: unknown) => {
    const lat = toFinite(latitude);
    const lon = toFinite(longitude);
    if (lat == null || lon == null) return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
    return { latitude: lat, longitude: lon };
  };

  if (Array.isArray(value)) {
    return build(value[0], value[1]);
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return build(record.latitude ?? record.lat, record.longitude ?? record.lon ?? record.lng);
  }

  const text = String(value).trim();
  const pointMatch = text.match(/POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)/i);
  if (pointMatch) {
    return build(pointMatch[2], pointMatch[1]);
  }

  const parts = text
    .replace(/[[\]{}]/g, ' ')
    .replace(/lat(?:itude)?\s*[:=]/ig, '')
    .replace(/lon(?:gitude)?\s*[:=]/ig, '')
    .replace(/lng\s*[:=]/ig, '')
    .split(/[;,\s]+/)
    .filter(Boolean);
  return parts.length >= 2 ? build(parts[0], parts[1]) : null;
};

const normalizeWeatherGap = (value: unknown): WeatherGapInfo | null => {
  if (!value || typeof value !== 'object') return null;
  const gap = value as WeatherGapInfo;
  return {
    ...gap,
    suggestions: Array.isArray(gap.suggestions) ? gap.suggestions : [],
    coordinates: normalizeCoordinates((gap as any).coordinates),
  };
};

/**
 * Распарсить ошибку RTK Query (или произвольную) и достать payload WEATHER_MAP_GAP, если есть.
 * Возвращает gap info или null.
 */
export const extractWeatherGap = (error: unknown): WeatherGapInfo | null => {
  if (!error || typeof error !== 'object') return null;
  // RTK Query FetchBaseQueryError: { status, data }
  const anyErr = error as Record<string, unknown>;
  const data = (anyErr.data ?? anyErr.error ?? anyErr.response ?? null) as Record<string, unknown> | null;
  if (data && typeof data === 'object' && (data as any).errorCode === 'WEATHER_MAP_GAP' && (data as any).gap) {
    return normalizeWeatherGap((data as any).gap);
  }
  // Иногда ошибка приходит уже как { errorCode, gap }
  if ((anyErr as any).errorCode === 'WEATHER_MAP_GAP' && (anyErr as any).gap) {
    return normalizeWeatherGap((anyErr as any).gap);
  }
  return null;
};
